import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/*
 * Cashfree Checkout helper.
 *
 *  payWithCashfree({ kind: "subscription" | "addon", id })
 *     1. asks our backend to create/reuse a Cashfree order for an existing payment intent / add-on order
 *        (the backend reads the amount from the database, never from the browser)
 *     2. loads the Cashfree JS SDK and opens the hosted checkout (full-page redirect)
 *     3. Cashfree redirects back to /payment/success?order_id=..., which verifies with the server.
 *
 * IMPORTANT: with redirectTarget "_self" the SDK promise can resolve BEFORE the browser has
 * navigated away. We must NOT navigate ourselves in that case, or we cancel the redirect to the
 * Cashfree payment page (this was the "cannot open payment page" bug).
 */
const SDK_URL = "https://sdk.cashfree.com/js/v3/cashfree.js";
const SDK_TIMEOUT_MS = 15000;

function loadSdk() {
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (fn, v) => { if (!settled) { settled = true; clearTimeout(timer); fn(v); } };
    const timer = setTimeout(() => done(reject, new Error("The payment window took too long to load. Check your connection or disable ad-blockers and try again.")), SDK_TIMEOUT_MS);
    // Remove a previous failed tag so a retry really re-downloads the SDK.
    document.querySelectorAll(`script[src="${SDK_URL}"]`).forEach((n) => n.remove());
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => (window.Cashfree ? done(resolve, window.Cashfree) : done(reject, new Error("Cashfree SDK missing")));
    s.onerror = () => done(reject, new Error("Could not load the payment window. Check your connection or disable ad-blockers."));
    document.head.appendChild(s);
  });
}

/** The page the customer started paying from. A failed / abandoned payment sends them back here. */
export const PAY_RETURN_KEY = "navi_pay_return";
function rememberReturnPage() {
  try {
    const here = window.location.pathname + window.location.search;
    if (!here.startsWith("/payment/")) sessionStorage.setItem(PAY_RETURN_KEY, here);
  } catch { /* private mode: server supplies a default page */ }
}
export function takeReturnPage(fallback = "/subscription") {
  try {
    const v = sessionStorage.getItem(PAY_RETURN_KEY);
    sessionStorage.removeItem(PAY_RETURN_KEY);
    if (v && v.startsWith("/") && !v.startsWith("//")) return v;
  } catch { /* ignore */ }
  return fallback;
}

export async function payWithCashfree({ kind, id }) {
  // Creating an order makes up to two calls to Cashfree, so allow more than the default 15 s.
  const { data } = await api.post("/payments/cashfree/order", { kind, id }, { timeout: 60000 });
  if (!data?.payment_session_id || !data?.order_id) {
    throw new Error("Payment session could not be created. Please try again.");
  }
  rememberReturnPage();
  const Cashfree = await loadSdk();
  const cashfree = Cashfree({ mode: data.mode === "production" ? "production" : "sandbox" });
  const result = await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });

  if (result?.error) {
    throw new Error(result.error.message || "Could not open the payment page. Please try again.");
  }
  if (result?.paymentDetails) {
    // Rare non-redirect completion: confirm with our server.
    window.location.assign(`/payment/success?order_id=${encodeURIComponent(data.order_id)}`);
    return;
  }
  // Redirect to Cashfree is in progress. Keep the caller's loading state until the page unloads;
  // if nothing happened after a few seconds, tell the user instead of leaving a silent spinner.
  await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("The payment page did not open. Please tap Pay again.")), 10000);
    window.addEventListener("pagehide", () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

/** true when the server has Cashfree keys configured, so the UI can show "Pay online". */
export function useCashfreeEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/payments/cashfree/config")
      .then(({ data }) => { if (alive) setEnabled(!!data?.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return enabled;
}
